import React, { useState, useEffect } from "react";
import GenericModal from "../../../components/buttons/genericModal";
import { Form, Input, Select, DatePicker } from "antd";
import { backendPatchWithoutRefresh, backendPostWithoutRefresh } from "../../../helpers/callBackend";
import moment from "moment";
import LoadingButton from "@mui/lab/LoadingButton";
import { Button } from "@mui/material";
import { customDateRangeReportTypes } from "./constants";

const AddOrEditReportButton = ({
  edit = false,
  name = "",
  type = "",
  groupingType = "",
  groupName = "",
  transactionGroupingType = "",
  startDate = null,
  endDate = null,
  numAccounts = 0,
  reportId = null,
  onSubmit,
  reportTypes = [],
  groupingTypeFilter = [],
  selectGroupFilter = [],
  selectGroups = [],
  setSelectGroups,
  reportType,
  setReportType,
  setGroupingType,
  setTransactionGroupingType,
  isTransactionReportType = {},
  transactionGroupingTypeFilter,
  loadingGroupingOptions,
}) => {
  const [visible, setVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState(endDate ? moment(endDate) : null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible && edit) {
      if (type) {
        setReportType(type);
      }
      if (groupingType) {
        setGroupingType(groupingType);
      }
    }
  }, [visible, type, groupingType, setReportType, setGroupingType]);

  const onFormSubmit = (values) => {
    setSubmitting(true);
    values.group_ids = values.group_ids.length > 5 ? values.group_ids.slice(0, 5) : values.group_ids;
    const data = {
      ...values,
      ...(edit ? {
        report_id: reportId,
      } : {}),
      ...(customDateRangeReportTypes.includes(values.type)
        ? {
            custom_start_date: moment
              .utc(values.date_range[0])
              .format("YYYY-MM-DD HH:mm:ss.SSSSSS"),
            custom_end_date: moment
              .utc(values.date_range[1])
              .format("YYYY-MM-DD HH:mm:ss.SSSSSS"),
          }
        : {}),
      ...(values.type === "BD" && selectedDate
        ? {
            custom_end_date: moment
              .utc(selectedDate)
              .format("YYYY-MM-DD HH:mm:ss.SSSSSS"),
          }
        : {}),
    };

    delete data.date_range;
    delete data.balance_as_of_date;

    const backendCall = edit ? backendPatchWithoutRefresh : backendPostWithoutRefresh;

    backendCall("/api/reports/", data).then((res) => {
      onSubmit(res.data?.data);
      setSubmitting(false);
      setVisible(false);
      form.resetFields();
    });
  };

  const triggerButton = (
    <Button
      sx={{ fontSize: 14, textTransform: "none" }}
      disableElevation
      fullWidth
      size="small"
      type="button"
      variant="contained"
      color="primary"
    >
      {edit ? "Edit Report" : "+ Add New"}
    </Button>
  );

  const handleModalOpen = () => {
    setVisible(true);
    if (edit) {
      const formValues = {
        name: name,
        type: type,
        grouping_type: groupingType,
        group_ids: selectGroups,
      };
      
      if (type === "CT" && startDate && endDate) {
        formValues.date_range = [moment(startDate), moment(endDate)];
      } else if (type === "BD" && endDate) {
        formValues.balance_as_of_date = moment(endDate);
        setSelectedDate(moment(endDate));
      }
      
      form.setFieldsValue(formValues);
      
      setReportType(type);
      setGroupingType(groupingType);
    }
  };

  return (
    <GenericModal
      visible={visible}
      handleCancelModal={() => {
        setVisible(false);
        form.resetFields();
      }}
      showModal={handleModalOpen}
      size="md"
      button={!edit ? triggerButton : <a>Edit</a>}
      modalTitle={edit ? "Edit Report" : "New Report"}
      modalBody={
        <Form form={form} layout="horizontal" colon={false} onFinish={onFormSubmit}>
          <Form.Item
            label={<label style={{ marginRight: 90 }}> Template Name: </label>}
            name="name"
            rules={[{ required: true }]}
            style={{ marginBottom: 10 }}
            initialValue={edit ? name : ""}
          >
            <Input
              style={{ width: 320 }}
              placeholder="Enter Report Name"
            />
          </Form.Item>

          <Form.Item
            label={<label style={{ marginRight: 111 }}> Report Type: </label>}
            name="type"
            rules={[{ required: true }]}
            style={{ marginBottom: 10 }}
            initialValue={edit ? type : undefined}
          >
            <Select
              style={{ width: 320 }}
              placeholder="Select Report Type"
              showSearch
              dropdownMatchSelectWidth={false}
              optionFilterProp="children"
              onChange={(e) => setReportType(e)}
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {reportTypes.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={<label style={{ marginRight: 73 }}> Account Grouping: </label>}
            name="grouping_type"
            rules={[{ required: true }]}
            style={{ marginBottom: 10 }}
            initialValue={edit ? groupingType : undefined}
          >
            <Select
              style={{ width: 320 }}
              placeholder="Select Account Grouping"
              showSearch
              optionFilterProp="children"
              dropdownMatchSelectWidth={false}
              onChange={(e) => {
                setGroupingType(e);
                setSelectGroups([]);
                form.setFieldsValue({ group_ids: [] });
              }}
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {groupingTypeFilter.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={<label style={{ marginRight: 149 }}> Group: </label>}
            name="group_ids"
            rules={[
              { required: true, message: "Please select at least one group!" },
              () => ({
                validator(_, value) {
                  if (value && value.length > 5) {
                    return Promise.reject(new Error("Only the first 5 groups will be considered!"));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
            style={{ marginBottom: 10 }}
            initialValue={edit ? selectGroups : []}
          >
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              style={{ width: 320 }}
              placeholder="Select Group"
              maxTagCount={5}
              dropdownMatchSelectWidth={false}
              initialValue={edit ? selectGroups : []}
              disabled={loadingGroupingOptions}
              loading={loadingGroupingOptions}
              onChange={(values) => {
                if (values.length > 5) {
                  setSelectGroups(values.slice(0, 5));
                } else {
                  setSelectGroups(values);
                }
              }}
            >
              {[...selectGroupFilter]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {item.name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>

          {isTransactionReportType[reportType] && (
            <Form.Item
              label={<label style={{ marginRight: 60 }}> Transaction Grouping: </label>}
              name="transaction_grouping_type"
              style={{ marginBottom: 10 }}
              initialValue={edit ? transactionGroupingType : undefined}
            >
              <Select
                style={{ width: 320 }}
                placeholder="Select Transaction Grouping"
                showSearch
                optionFilterProp="children"
                dropdownMatchSelectWidth={false}
                onChange={(e) => {
                  setTransactionGroupingType(e);
                }}
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {transactionGroupingTypeFilter.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {customDateRangeReportTypes.includes(reportType) && (
            <Form.Item
              label={<label style={{ marginRight: 73 }}> Date Range: </label>}
              rules={[{ required: true }]}
              name="date_range"
              initialValue={edit && startDate && endDate ? [moment(startDate), moment(endDate)] : undefined}
            >
              <DatePicker.RangePicker
                style={{ width: 320, marginLeft: 41 }}
                placeholder={["Start date", "End date"]}
              />
            </Form.Item>
          )}

          {reportType === "BD" && (
            <Form.Item
              label={<label style={{ marginRight: 70 }}> Balance as of Date: </label>}
              rules={[{ required: true }]}
              name="balance_as_of_date"
              initialValue={edit && endDate ? moment(endDate) : undefined}
            >
              <DatePicker
                style={{ width: 320 }}
                onChange={(date) => setSelectedDate(date)}
                placeholder="Select Date"
              />
            </Form.Item>
          )}

          <Form.Item>
            <LoadingButton
              htmlType="submit"
              style={{ marginLeft: 10, marginTop: 10 }}
              loading={submitting}
              loadingPosition="start"
              sx={{ fontSize: 14, textTransform: "none", width: "100px" }}
              disableElevation
              fullWidth
              size="small"
              type="submit"
              variant="contained"
              color="primary"
            >
              Submit
            </LoadingButton>
          </Form.Item>
        </Form>
      }
    />
  );
};

export default AddOrEditReportButton;